import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerLeavesTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { useNavigate } from 'react-router-dom';
import { GETLeaves, ILeave, PATCHLeaveStatus } from '../../services/leave';
import { debounce } from '../../utils/helper';
import DateFilterComp, {
  DateFilterData,
} from '../../components/ui/filters/enquiry';
import useSnackbar from '../../hooks/useSnackbar';
import AddLeaveRequestForm from '../../components/ui/add/leave';
import { LeaveList } from '../../components/ui/list/Leave';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const LeavesandHolidaysComp = () => {
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } = usePaginationParams();
  const [leaves, setLeaves] = useState<ILeave[]>([]);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('DESC');
  const [tabValue, setTabValue] = useState<string>(
    () => searchParams.get('tab') ?? 'ALL',
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);
  const { showSnackbar, SnackbarComponent, resetSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const fetchLeaves = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
    leaveType?: string,
    startDate?: Date | null,
    endDate?: Date | null,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETLeaves({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        leaveType,
        startDate,
        endDate,
      });

      // Ensure data is an array
      setLeaves(Array.isArray(response?.data) ? response.data : []);
      setTotalLeaves(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
      setLeaves([]);
      setTotalLeaves(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRange(tabValue);
    if (!filterFlag) {
      fetchLeaves(
        page,
        rowsPerPage,
        searchQuery,
        sort,
        GetTabFilter(tabValue),
        startDate,
        endDate,
      );
    }
  }, [page, rowsPerPage, searchQuery, sort, tabValue]);

  useEffect(() => {
    if (filterFlag) {
      fetchLeaves(
        page,
        rowsPerPage,
        searchQuery,
        sort,
        GetTabFilter(tabValue),
        startDate,
        endDate,
      );
    }
  }, [startDate, endDate, page, rowsPerPage, searchQuery, sort, tabValue]);

  const reloadDefaultList = () => {
    const { startDate, endDate } = getDateRange(tabValue);
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    fetchLeaves(
      page,
      rowsPerPage,
      searchQuery,
      sort,
      GetTabFilter(tabValue),
      startDate,
      endDate,
    );
  };

  const handleSaveRefresh = () => {
    reloadDefaultList();
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleOpenManageDrawer = async (id: string, action: string) => {
    if (!id) {
      showSnackbar('Invalid leave ID', 'error');
      return;
    }

    if (action === 'VIEW') {
      navigate(`/admin/technician/${id}/leave-details`);
    } else if (action === 'APPROVED' || action === 'REJECTED') {
      try {
        await PATCHLeaveStatus(id, action);
        reloadDefaultList();
        showSnackbar(`Leave ${action.toLowerCase()} successfully`, 'success');
      } catch (err: any) {
        showSnackbar(err?.message || 'Action failed', 'error');
      }
    }
  };

  const handleTabChange = (event: any, newValue: string) => {
    setFilterFlag(false);
    setStartDate(null);
    setEndDate(null);
    setTabValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', newValue);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const handleFilterRefresh = (Filter: DateFilterData) => {
    if (Filter.startDate && Filter.endDate) {
      setStartDate(new Date(Filter.startDate));
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}

      <SmartFilter
        title="Leaves"
        createFilter={['SEARCH', 'FILTER', 'ADD']}
        filterHeader={'Filter Services'}
        addbuttontitle="Request Leave"
        addHeader={'Request Leave'}
        addElement={<AddLeaveRequestForm />}
        filterElement={
          <DateFilterComp
            tabValue={tabValue}
            onResetFilter={reloadDefaultList}
            startDate={
              startDate
                ? new Date(startDate).toISOString().split('T')[0]
                : null
            }
            endDate={
              endDate ? new Date(endDate).toISOString().split('T')[0] : null
            }
          />
        }
        onSaved={handleSaveRefresh}
        onSearch={handleSearchRefresh}
        onFiltered={handleFilterRefresh}
        addDrawerSize="md"
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      <Box display="flex" justifyContent="center" width="100%" my={2}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={tabStyles}>
          <Tab value="ALL" label="All Requests" />
          <Tab value="SICK_LEAVE" label="Sick Leave" />
          <Tab value="CASUAL_LEAVE" label="Casual Leave" />
          <Tab value="PAID_LEAVE" label="Paid Leave" />
        </Tabs>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {!isLoading && totalLeaves === 0 ? (
          <Typography
            textAlign={'center'}
            marginTop={'20%'}
            color="textSecondary"
          >
            No Leave Request Found
          </Typography>
        ) : (
          <LeaveList
            Leaves={leaves}
            headerCells={headerLeavesTable || []}
            isLoading={isLoading}
            onManageClick={handleOpenManageDrawer}
          />
        )}
      </Box>
      <Box display={'block'} bottom={0} right={0} mt={2}>
        <Pagination
          count={totalLeaves}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(LeavesandHolidaysComp);

export const tabStyles = {
  backgroundColor: '#1B1B1B',
  borderRadius: '8px',
  padding: '6px',
  display: 'inline-flex',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    color: '#fff',
    textTransform: 'none',
    fontSize: '14px',
    padding: '10px 16px',
    minWidth: 'unset',
  },
  '& .Mui-selected': {
    color: '#fff !important',
    borderRadius: '8px',
    backgroundColor: '#d32f2f',
  },
};

const getDateRange = (tabValue: string) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  switch (tabValue) {
    case 'TODAY':
      return {
        startDate: today,
        endDate: today,
      };
    case 'OPEN_CALLS':
      return {
        startDate: null,
        endDate: yesterday,
      };
    default:
      return { startDate: null, endDate: null };
  }
};

const GetTabFilter = (tabValue: string) => {
  switch (tabValue) {
    case 'ALL':
      return 'SICK_LEAVE,CASUAL_LEAVE,PAID_LEAVE';
    case 'SICK_LEAVE':
      return 'SICK_LEAVE';
    case 'CASUAL_LEAVE':
      return 'CASUAL_LEAVE';
    case 'PAID_LEAVE':
      return 'PAID_LEAVE';
    default:
      return '';
  }
};
